import { useState, useEffect, lazy, Suspense } from "react";
import { Heart, Search, CalendarCheck, ShieldCheck, ArrowRight, Leaf, Lock, UserCheck, MessageCircle, Sparkles, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import FloatingLeaves from "@/components/FloatingLeaves";
import OrganicBlob from "@/components/OrganicBlob";
import { useIsMobile } from "@/hooks/use-mobile";
// Heavy decorative components — defer so they don't block the initial mobile paint
const FlyingBird = lazy(() => import("@/components/FlyingBird"));
const ForestBackground = lazy(() => import("@/components/ForestBackground"));
import { LeafDetailed, RootBase, Sprout, Seeds, OrganicHills, Branch, VineDivider, FooterRoots, Twig, MossCluster } from "@/components/DecorativeElements";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.55, ease: [0.22, 1, 0.36, 1] as const },
  }),
};

const benefits = [
  {
    icon: UserCheck,
    title: "Terapeutas verificados",
    description: "Profissionais qualificados e avaliados pela nossa equipe.",
  },
  {
    icon: CalendarCheck,
    title: "Agendamento fácil",
    description: "Encontre horários disponíveis e agende em poucos cliques.",
  },
  {
    icon: Heart,
    title: "Acompanhe seu progresso",
    description: "Diário emocional e histórico completo da sua jornada.",
  },
  {
    icon: Lock,
    title: "Privacidade garantida",
    description: "Seus dados são protegidos com total sigilo e segurança.",
  },
];

const steps = [
  { number: "01", title: "Crie sua conta", description: "Cadastre-se de forma rápida e gratuita." },
  { number: "02", title: "Encontre seu terapeuta", description: "Explore perfis e escolha quem mais combina com você." },
  { number: "03", title: "Agende sua sessão", description: "Selecione o melhor horário e confirme seu atendimento." },
  { number: "04", title: "Cuide de você", description: "Acompanhe sua evolução e cultive seu bem-estar." },
];

const particles = [
  { size: 6, left: "12%", top: "20%", duration: 9, delay: 0, opacity: 0.35 },
  { size: 4, left: "25%", top: "60%", duration: 11, delay: 1.5, opacity: 0.25 },
  { size: 8, left: "70%", top: "15%", duration: 8, delay: 0.5, opacity: 0.3 },
  { size: 5, left: "85%", top: "55%", duration: 13, delay: 2, opacity: 0.2 },
  { size: 7, left: "50%", top: "80%", duration: 10, delay: 3, opacity: 0.28 },
  { size: 3, left: "90%", top: "30%", duration: 7, delay: 1, opacity: 0.22 },
  { size: 5, left: "35%", top: "45%", duration: 12, delay: 4, opacity: 0.18 },
];

const toolTexts = [
  "Teste de TDAH",
  "Teste de ansiedade",
  "Teste de QI",
  "Teste de burnout",
  "Teste de estilo de apego emocional",
  "Teste de overthinking",
  "Teste de autismo em adultos",
];

export default function LandingPage() {
  const [toolIndex, setToolIndex] = useState(0);
  const isMobile = useIsMobile();

  useEffect(() => {
    const interval = setInterval(() => {
      setToolIndex((prev) => (prev + 1) % toolTexts.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <main className="min-h-screen bg-background overflow-x-hidden relative">
      {/* Immersive forest background layers — desktop only (heavy SVG masking) */}
      {!isMobile && (
        <Suspense fallback={null}>
          <ForestBackground />
        </Suspense>
      )}

      {/* Flying birds — desktop only */}
      {!isMobile && (
        <Suspense fallback={null}>
          <FlyingBird />
        </Suspense>
      )}

      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border/50 bg-background/70 backdrop-blur-xl">
        <div className="container mx-auto px-2 sm:px-4">
          {/* Mobile layout — minimalista: logo centralizada + menu */}
          <div className="md:hidden">
            <div className="relative flex h-14 items-center justify-center w-full">
              {/* Logo centralizada */}
              <Link to="/" className="flex items-center gap-2">
                <Leaf className="h-5 w-5 shrink-0 text-primary" />
                <span className="whitespace-nowrap font-heading text-base font-semibold tracking-tight text-foreground">
                  Jardim Mental
                </span>
              </Link>

              {/* Menu hambúrguer no canto direito */}
              <Sheet>
                <SheetTrigger asChild>
                  <button
                    aria-label="Abrir menu"
                    className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex h-9 w-9 items-center justify-center rounded-lg text-foreground/80 hover:bg-accent/40 transition-colors"
                  >
                    <Menu className="h-5 w-5" />
                  </button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[78%] max-w-xs p-0 flex flex-col">
                  <SheetHeader className="px-5 pt-5 pb-3 border-b border-border/40">
                    <SheetTitle className="flex items-center gap-2 text-base font-heading">
                      <Leaf className="h-4 w-4 text-primary" />
                      Jardim Mental
                    </SheetTitle>
                  </SheetHeader>
                  <nav className="flex flex-col p-4 gap-1.5">
                    <SheetClose asChild>
                      <Link to="/paciente/auth" className="rounded-xl bg-primary text-primary-foreground text-center py-3 text-sm font-semibold shadow-soft">
                        Entrar
                      </Link>
                    </SheetClose>
                    <SheetClose asChild>
                      <Link to="/paciente/auth" className="rounded-xl bg-accent text-accent-foreground text-center py-3 text-sm font-semibold">
                        Começar
                      </Link>
                    </SheetClose>
                    <div className="h-px bg-border/50 my-3" />
                    <SheetClose asChild>
                      <Link to="/diretorio" className="rounded-lg px-3 py-3 text-sm text-foreground/90 hover:bg-accent/40 transition-colors">
                        Encontrar terapeutas
                      </Link>
                    </SheetClose>
                    <SheetClose asChild>
                      <Link to="/ferramentas" className="rounded-lg px-3 py-3 text-sm text-foreground/90 hover:bg-accent/40 transition-colors">
                        Ferramentas gratuitas
                      </Link>
                    </SheetClose>
                    <SheetClose asChild>
                      <a href="#sobre" className="rounded-lg px-3 py-3 text-sm text-foreground/90 hover:bg-accent/40 transition-colors">
                        Sobre
                      </a>
                    </SheetClose>
                    <div className="h-px bg-border/50 my-3" />
                    <SheetClose asChild>
                      <Link to="/terapeuta/auth" className="rounded-lg px-3 py-2.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
                        Sou terapeuta
                      </Link>
                    </SheetClose>
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
          </div>

          {/* Desktop layout (3 áreas equilibradas) */}
          <div className="hidden md:grid md:h-16 md:grid-cols-[1fr_auto_1fr] md:items-center">
            {/* Esquerda */}
            <Link to="/" className="flex items-center gap-2.5 justify-self-start">
              <Leaf className="h-5 w-5 shrink-0 text-primary" />
              <span className="whitespace-nowrap font-heading text-lg font-semibold tracking-tight text-foreground">
                Jardim Mental
              </span>
            </Link>

            {/* Centro */}
            <div className="flex items-center justify-center gap-8">
              <Link to="/diretorio" className="text-sm text-muted-foreground transition-colors hover:text-foreground whitespace-nowrap">
                Encontrar Terapeuta
              </Link>
              <Link to="/ferramentas" className="text-sm text-muted-foreground transition-colors hover:text-foreground whitespace-nowrap">
                Ferramentas gratuitas
              </Link>
            </div>

            {/* Direita */}
            <div className="flex items-center gap-3 justify-self-end">
              <Link
                to="/terapeuta/auth"
                className="whitespace-nowrap text-xs text-muted-foreground transition-colors hover:text-foreground"
              >
                Sou terapeuta
              </Link>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/paciente/auth">Entrar</Link>
              </Button>
              <Button size="sm" asChild>
                <Link to="/paciente/auth">Começar</Link>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* ── HERO MOBILE (primeira dobra, sem scroll) ── */}
      <section className="md:hidden relative gradient-hero overflow-hidden flex flex-col justify-center min-h-[calc(100vh-3.5rem)] pt-20 pb-10 px-5">
        {/* Decorativo leve — folhas e galhos nas bordas */}
        <Branch className="w-32 h-20 -top-2 -left-6" delay={0} />
        <Branch className="w-28 h-18 -top-2 -right-6" delay={1.2} flip />
        <LeafDetailed className="w-12 h-12 top-24 left-2 opacity-70" delay={0} variant={0} />
        <LeafDetailed className="w-14 h-14 bottom-16 right-3 opacity-70" delay={1} variant={3} color="hsl(var(--olive))" />
        <Sprout className="w-8 h-12 bottom-6 left-6 opacity-80" delay={0.5} />
        <MossCluster className="w-20 h-10 bottom-2 right-[18%]" />
        <FloatingLeaves count={6} />

        <OrganicBlob className="-top-20 -left-20" color="hsl(160, 40%, 80%)" size="320px" duration={11} delay={0} blur="80px" />
        <OrganicBlob className="-bottom-16 -right-12" color="hsl(265, 30%, 84%)" size="280px" duration={13} delay={2} blur="70px" />

        <div className="relative z-30 w-full max-w-sm mx-auto text-center flex flex-col gap-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-3"
          >
            <div className="inline-flex items-center gap-1.5 bg-sage-light rounded-full px-3 py-1.5 shadow-soft">
              <Heart className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs font-semibold text-primary tracking-wide">
                Sua jornada começa aqui
              </span>
            </div>
            <h1 className="font-heading text-[2.15rem] leading-[1.15] font-bold text-foreground tracking-tight">
              Comece sua <span className="text-gradient">terapia agora</span>
            </h1>
            <p className="text-base text-foreground/80 leading-relaxed px-1 font-medium">
              Conecte-se com terapeutas qualificados em um ambiente seguro e acolhedor.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col gap-3"
          >
            <Button variant="hero" size="xl" className="w-full h-14 text-base btn-breathe" asChild>
              <Link to="/paciente/auth">Entrar</Link>
            </Button>
            <Button variant="warm" size="xl" className="w-full h-14 text-base" asChild>
              <Link to="/paciente/auth">Começar</Link>
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="flex items-center justify-center"
          >
            <Link
              to="/terapeuta/auth"
              className="text-xs font-medium text-foreground/70 hover:text-foreground transition-colors underline-offset-4 hover:underline"
            >
              Sou terapeuta
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── EXPLORAÇÃO MOBILE (navegação secundária) ── */}
      <section className="md:hidden relative px-5 py-8 bg-background/60">
        <div className="max-w-sm mx-auto grid grid-cols-2 gap-3">
          <Link
            to="/diretorio"
            className="card-garden flex flex-col items-start gap-2 p-4 rounded-2xl bg-card border border-border/50 shadow-soft"
          >
            <div className="w-9 h-9 rounded-xl bg-sage-light flex items-center justify-center">
              <Search className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="font-heading font-semibold text-base text-foreground leading-tight">
                Encontrar terapeutas
              </p>
              <p className="text-xs text-foreground/70 mt-1 font-medium">Explore profissionais</p>
            </div>
          </Link>
          <Link
            to="/ferramentas"
            className="card-garden flex flex-col items-start gap-2 p-4 rounded-2xl bg-card border border-border/50 shadow-soft"
          >
            <div className="w-9 h-9 rounded-xl bg-accent flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="font-heading font-semibold text-base text-foreground leading-tight">
                Ferramentas gratuitas
              </p>
              <p className="text-xs text-foreground/70 mt-1 font-medium">Testes e checkups</p>
            </div>
          </Link>
        </div>
      </section>

      {/* ── HERO (DESKTOP) ── */}
      <section id="sobre" className="hidden md:block relative pt-28 pb-32 gradient-hero overflow-hidden">

        <FloatingLeaves count={22} />

        {/* Branches at top corners — pushed to edges */}
        <Branch className="w-48 h-32 -top-4 -left-10" delay={0} />
        <Branch className="w-40 h-28 -top-4 -right-8" delay={1.5} flip />

        {/* Leaves repositioned to corners/edges — away from center text */}
        <LeafDetailed className="w-16 h-16 top-24 left-2" delay={0} variant={0} />
        <LeafDetailed className="w-20 h-20 top-32 right-2" delay={1} variant={3} color="hsl(var(--olive))" />
        <LeafDetailed className="w-12 h-12 bottom-48 left-[3%]" delay={2} variant={4} />
        <LeafDetailed className="w-14 h-14 bottom-36 right-[4%]" delay={0.5} variant={1} color="hsl(var(--olive))" />

        {/* Twig on side */}
        <Twig className="w-10 h-20 top-40 left-0" delay={1} />

        {/* Sprouts & Seeds in corners */}
        <Sprout className="w-10 h-14 bottom-24 left-8" delay={0.5} />
        <Sprout className="w-8 h-12 bottom-20 right-10" delay={2} color="hsl(var(--sage))" />
        <Seeds className="w-28 h-20 bottom-12 left-[15%]" />
        <MossCluster className="w-24 h-12 bottom-4 right-[12%]" />

        {/* Roots at base */}
        

        {/* Ambient particles */}
        {particles.map((p, i) => (
          <span
            key={i}
            className="particle absolute rounded-full bg-primary/30"
            style={{
              width: p.size,
              height: p.size,
              left: p.left,
              top: p.top,
              "--duration": `${p.duration}s`,
              "--delay": `${p.delay}s`,
              "--opacity": p.opacity,
            } as React.CSSProperties}
          />
        ))}

        {/* Organic blobs */}
        <OrganicBlob className="-top-32 -left-32" color="hsl(160, 40%, 78%)" size="520px" duration={10} delay={0} blur="90px" />
        <OrganicBlob className="-bottom-24 right-0" color="hsl(265, 30%, 82%)" size="400px" duration={12} delay={3} blur="80px" />
        <OrganicBlob className="top-1/4 right-1/4" color="hsl(210, 55%, 88%)" size="300px" duration={9} delay={1.5} blur="70px" />

        <div className="container relative z-30 mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div initial="hidden" animate="visible" className="space-y-6">
              <motion.div variants={fadeUp} custom={0} className="inline-flex items-center gap-2 bg-sage-light rounded-full px-4 py-1.5">
                <Heart className="h-3.5 w-3.5 text-primary" />
                <span className="text-xs font-medium text-primary tracking-wide">Sua jornada de cuidado começa aqui</span>
              </motion.div>

              <motion.h1 variants={fadeUp} custom={1} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-[1.15] tracking-tight">
                Encontre o apoio terapêutico{" "}
                <span className="text-gradient">que você merece</span>
              </motion.h1>

              <motion.p variants={fadeUp} custom={2} className="text-lg text-muted-foreground leading-relaxed max-w-xl mx-auto">
                Conecte-se com terapeutas qualificados, agende sessões com facilidade e acompanhe sua evolução emocional em um ambiente seguro e acolhedor.
              </motion.p>

              <motion.div variants={fadeUp} custom={3} className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
                <Button variant="hero" size="xl" className="btn-breathe" asChild>
                  <Link to="/diretorio">
                    Encontrar um terapeuta <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </Button>
                <Button variant="warm" size="xl" className="min-w-[260px] flex-col h-auto py-3" asChild>
                  <Link to="/ferramentas">
                    <span className="flex items-center gap-1.5">
                      <Sparkles className="h-4 w-4" />
                      Ferramentas gratuitas
                    </span>
                    <AnimatePresence mode="wait">
                      <motion.span
                        key={toolIndex}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.4 }}
                        className="block text-xs font-normal opacity-75"
                      >
                        {toolTexts[toolIndex]}
                      </motion.span>
                    </AnimatePresence>
                  </Link>
                </Button>
              </motion.div>
            </motion.div>
          </div>

          {/* Decorative card */}
          <div className="relative mt-16 flex justify-center">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="relative"
            >
              <div className="w-72 h-72 rounded-full bg-sage-light/60 animate-breathe absolute -top-10 -left-16 blur-2xl" />
              <div className="w-48 h-48 rounded-full bg-lavender/60 animate-breathe absolute -bottom-6 -right-8 blur-2xl" style={{ animationDelay: "2s" }} />
              <div className="w-56 h-56 rounded-full bg-sky-light/60 animate-breathe absolute top-4 right-12 blur-2xl" style={{ animationDelay: "3s" }} />
              <div className="relative z-30 bg-card rounded-3xl shadow-elevated p-8 max-w-md mx-auto border border-border/50">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-sage-light flex items-center justify-center">
                    <MessageCircle className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Sua próxima sessão</p>
                    <p className="text-xs text-muted-foreground">Amanhã às 14:00</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="h-2 rounded-full bg-sage-light w-full" />
                  <div className="h-2 rounded-full bg-lavender w-3/4" />
                  <div className="h-2 rounded-full bg-sky-light w-1/2" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vine divider */}
      <VineDivider />

      {/* ── BENEFITS ── */}
      <section className="relative py-24 overflow-hidden">
        {/* Leaves on edges only */}
        <LeafDetailed className="w-14 h-14 top-12 right-4" delay={0.5} variant={3} />
        <LeafDetailed className="w-16 h-16 bottom-16 left-2" delay={1.5} variant={4} color="hsl(var(--olive))" />
        <LeafDetailed className="w-10 h-10 top-1/2 right-1" delay={2.5} variant={2} />
        <Sprout className="w-8 h-12 top-20 left-4" delay={2} />
        <Twig className="w-8 h-16 bottom-8 right-2" delay={1} />
        <MossCluster className="w-20 h-10 bottom-2 left-[8%]" />
        <Seeds className="w-32 h-24 bottom-4 right-[5%]" />
        <Branch className="w-32 h-20 bottom-0 -left-10 opacity-60" delay={3} color="hsl(var(--olive))" />

        <OrganicBlob className="top-0 left-1/2 -translate-x-1/2" color="hsl(160, 30%, 92%)" size="600px" duration={14} delay={2} blur="100px" />

        <div className="container relative z-30 mx-auto px-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
            <motion.p variants={fadeUp} custom={0} className="text-xs md:text-sm font-bold md:font-medium text-primary tracking-[0.15em] mb-3">POR QUE O JARDIM MENTAL</motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-heading text-[1.75rem] leading-tight md:text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Um espaço seguro para cuidar de você
            </motion.h2>
            <motion.p variants={fadeUp} custom={2} className="text-foreground/80 md:text-muted-foreground text-base max-w-lg mx-auto font-medium md:font-normal">
              Tudo o que você precisa para iniciar e manter seu acompanhamento terapêutico, em um só lugar.
            </motion.p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {benefits.map((b, i) => (
              <motion.div
                key={b.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="card-garden bg-card rounded-2xl p-6 shadow-card border border-border/50 group"
              >
                <motion.div
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: i * 0.8 }}
                  className="w-11 h-11 rounded-xl bg-sage-light flex items-center justify-center mb-4 group-hover:bg-primary/10 transition-colors duration-300"
                >
                  <b.icon className="h-5 w-5 text-primary" />
                </motion.div>
                <h3 className="font-heading font-semibold text-lg md:text-base text-foreground mb-1.5">{b.title}</h3>
                <p className="text-foreground/80 md:text-muted-foreground text-[15px] md:text-sm leading-relaxed">{b.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vine divider */}
      <VineDivider color="hsl(var(--olive))" />

      {/* ── HOW IT WORKS ── */}
      <section className="relative py-24 gradient-lavender overflow-hidden">
        
        <Branch className="w-44 h-28 -top-4 -right-6" delay={1} flip color="hsl(var(--primary))" />
        <LeafDetailed className="w-14 h-14 top-16 left-2" variant={4} delay={0.5} color="hsl(var(--primary))" />
        <LeafDetailed className="w-10 h-10 bottom-20 right-4" variant={3} delay={2} />
        <Sprout className="w-8 h-12 bottom-28 right-6" delay={1.5} color="hsl(var(--primary))" />
        <Twig className="w-8 h-18 top-24 right-0" delay={2.5} color="hsl(var(--primary))" />
        <MossCluster className="w-16 h-8 bottom-4 left-4" color="hsl(var(--primary))" />
        <FloatingLeaves count={10} />

        <OrganicBlob className="-bottom-20 -right-20" color="hsl(160, 35%, 82%)" size="350px" duration={11} delay={1} blur="80px" />

        <div className="container relative z-30 mx-auto px-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
            <motion.p variants={fadeUp} custom={0} className="text-xs md:text-sm font-bold md:font-medium text-primary tracking-[0.15em] mb-3">COMO FUNCIONA</motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-heading text-[1.75rem] leading-tight md:text-3xl lg:text-4xl font-bold text-foreground">
              Comece em poucos passos
            </motion.h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {steps.map((s, i) => (
              <motion.div
                key={s.number}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="text-center group relative z-10 rounded-2xl bg-card/70 md:bg-transparent backdrop-blur-sm md:backdrop-blur-none border border-border/40 md:border-0 p-5 md:p-0 shadow-soft md:shadow-none"
              >
                <motion.span
                  animate={{ scale: [1, 1.04, 1] }}
                  transition={{ duration: 7 + i, repeat: Infinity, ease: "easeInOut", delay: i * 1.2 }}
                  className="inline-block text-4xl font-heading font-bold text-gradient"
                >
                  {s.number}
                </motion.span>
                <h3 className="font-heading font-semibold text-lg md:text-base text-foreground mt-3 mb-1.5">{s.title}</h3>
                <p className="text-[15px] md:text-sm text-foreground/80 md:text-muted-foreground leading-relaxed">{s.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TRUST ── */}
      <section className="relative py-24 overflow-hidden">
        <LeafDetailed className="w-16 h-16 -top-2 -left-2" delay={0.8} variant={0} />
        <LeafDetailed className="w-12 h-12 bottom-12 right-4" delay={1.2} variant={3} color="hsl(var(--olive))" />
        <LeafDetailed className="w-10 h-10 top-20 right-2" delay={2} variant={4} />
        <Branch className="w-28 h-18 bottom-2 -left-8 opacity-50" delay={2} />
        <Twig className="w-6 h-14 top-1/3 left-0" delay={3} />
        <MossCluster className="w-20 h-10 bottom-0 right-[20%]" />
        

        <OrganicBlob className="top-10 right-0" color="hsl(265, 25%, 88%)" size="380px" duration={13} delay={0.5} blur="90px" />

        <div className="container relative z-30 mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
              <motion.h2 variants={fadeUp} custom={0} className="font-heading text-[1.75rem] leading-tight md:text-3xl lg:text-4xl font-bold text-foreground mb-4">
                Sua segurança é nossa prioridade
              </motion.h2>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid sm:grid-cols-3 gap-6">
              {[
                { icon: ShieldCheck, title: "Dados protegidos", text: "Criptografia e sigilo total das suas informações." },
                { icon: UserCheck, title: "Profissionais verificados", text: "Todos os terapeutas passam por verificação." },
                { icon: Heart, title: "Feito para você", text: "Uma plataforma pensada para apoiar sua jornada emocional." },
              ].map((item, i) => (
                <motion.div
                  key={item.title}
                  variants={fadeUp}
                  custom={i}
                  className="card-garden text-center p-6 bg-card rounded-2xl border border-border/40 shadow-soft"
                >
                  <motion.div
                    animate={{ scale: [1, 1.06, 1] }}
                    transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: i * 1.5 }}
                    className="w-12 h-12 rounded-2xl bg-sage-light flex items-center justify-center mx-auto mb-4"
                  >
                    <item.icon className="h-5 w-5 text-primary" />
                  </motion.div>
                  <h3 className="font-heading font-semibold text-lg md:text-base text-foreground mb-1">{item.title}</h3>
                  <p className="text-[15px] md:text-sm text-foreground/80 md:text-muted-foreground leading-relaxed">{item.text}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vine divider */}
      <VineDivider />

      {/* ── CTA FINAL ── */}
      <section className="relative py-24 gradient-warm overflow-hidden">
        <Branch className="w-48 h-28 -top-4 -left-12" delay={0} color="hsl(var(--olive))" />
        <Branch className="w-36 h-24 -top-2 -right-10" delay={1.5} flip />
        <LeafDetailed className="w-20 h-20 -bottom-4 -right-4" delay={0} variant={3} color="hsl(var(--olive))" />
        <LeafDetailed className="w-14 h-14 top-16 left-4" delay={2} variant={4} />
        <LeafDetailed className="w-10 h-10 bottom-24 left-2" delay={1} variant={1} />
        <Sprout className="w-10 h-14 bottom-16 left-[18%]" delay={1} />
        <Twig className="w-8 h-16 bottom-8 right-4" delay={2.5} />
        <MossCluster className="w-24 h-12 bottom-2 left-[40%]" color="hsl(var(--olive))" />
        <Seeds className="w-36 h-24 top-8 right-[8%]" />
        
        <FloatingLeaves count={12} />

        <OrganicBlob className="-top-16 left-0" color="hsl(32, 55%, 88%)" size="420px" duration={10} delay={2} blur="80px" />
        <OrganicBlob className="-bottom-20 right-10" color="hsl(265, 30%, 88%)" size="340px" duration={12} delay={1} blur="70px" />

        <div className="container relative z-30 mx-auto px-4 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.h2 variants={fadeUp} custom={0} className="font-heading text-[1.75rem] leading-tight md:text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Comece seu acompanhamento hoje
            </motion.h2>
            <motion.p variants={fadeUp} custom={1} className="text-foreground text-base md:text-lg font-semibold md:font-medium max-w-sm mx-auto mb-8">
              Dê o primeiro passo em direção ao seu bem-estar emocional. Estamos aqui por você.
            </motion.p>
            <motion.div variants={fadeUp} custom={2} className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button variant="hero" size="xl" className="btn-breathe" asChild>
                <Link to="/diretorio">
                  Encontrar um terapeuta <Search className="ml-1 h-4 w-4" />
                </Link>
              </Button>
              <Button variant="warm" size="xl" className="min-w-[260px] flex-col h-auto py-3" asChild>
                <Link to="/ferramentas">
                  <span className="flex items-center gap-1.5">
                    <Sparkles className="h-4 w-4" />
                    Ferramentas gratuitas
                  </span>
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={toolIndex}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.4 }}
                      className="block text-xs font-normal opacity-75"
                    >
                      {toolTexts[toolIndex]}
                    </motion.span>
                  </AnimatePresence>
                </Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── FOOTER with GRASSLAND ── */}
      <footer className="relative py-16 overflow-hidden">
        {/* ── GRASSLAND BACKGROUND ── */}
        <div className="absolute inset-0 z-[26] pointer-events-none overflow-hidden">
          <svg viewBox="0 0 1600 400" fill="none" preserveAspectRatio="none" className="w-full h-full">
            <defs>
              <linearGradient id="grassBase" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#5A8A58" />
                <stop offset="30%" stopColor="#4A7A48" />
                <stop offset="60%" stopColor="#3D6B3A" />
                <stop offset="100%" stopColor="#2E5A2C" />
              </linearGradient>
            </defs>
            
            {/* Base terrain layers */}
            <rect x="0" y="0" width="1600" height="400" fill="url(#grassBase)" />
            <path d="M0,30 C200,10 400,50 600,25 C800,0 1000,45 1200,20 C1400,0 1500,35 1600,15 L1600,400 L0,400 Z" fill="#4A7848" />
            <path d="M0,60 C150,35 350,75 550,45 C750,15 950,65 1150,40 C1350,18 1500,55 1600,38 L1600,400 L0,400 Z" fill="#3D6B3A" />
            <path d="M0,90 C250,60 500,110 750,75 C1000,45 1250,95 1600,68 L1600,400 L0,400 Z" fill="#4A7A48" />
            
            {/* Highlight patches */}
            <ellipse cx="300" cy="80" rx="180" ry="50" fill="#6B9E5A" opacity="0.3" />
            <ellipse cx="800" cy="60" rx="220" ry="55" fill="#7AAE6A" opacity="0.25" />
            <ellipse cx="1300" cy="90" rx="160" ry="45" fill="#6B9E5A" opacity="0.28" />
            
            {/* Shadow patches */}
            <ellipse cx="500" cy="120" rx="140" ry="40" fill="#2E5A2C" opacity="0.2" />
            <ellipse cx="1050" cy="100" rx="170" ry="45" fill="#2E5A2C" opacity="0.18" />
            
            {/* Dense grass blades — top edge */}
            {[0,28,55,82,108,135,160,188,215,242,268,295,320,348,375,402,428,455,480,508,535,562,588,615,640,668,695,722,748,775,800,828,855,882,908,935,960,988,1015,1042,1068,1095,1120,1148,1175,1202,1228,1255,1280,1308,1335,1362,1388,1415,1440,1468,1495,1522,1548,1575].map((x, i) => (
              <g key={i} opacity={0.7 + (i % 4) * 0.06}>
                <path d={`M${x},${28 - i%7*3} Q${x - 4 - i%3*2},${12 - i%5*3} ${x - 8 - i%4*3},${-5 - i%3*5}`} stroke="#4A8A48" strokeWidth="2.2" strokeLinecap="round" fill="none" />
                <path d={`M${x+8},${30 - i%5*2} Q${x+14 + i%2*2},${14 - i%4*3} ${x+18 + i%3*2},${-2 - i%5*4}`} stroke="#5C9E5B" strokeWidth="1.8" strokeLinecap="round" fill="none" />
                <path d={`M${x+16},${26 - i%3*3} Q${x+20 + i%2},${10 - i%4*3} ${x+24},${-4 - i%3*4}`} stroke="#6BAE6B" strokeWidth="1.4" strokeLinecap="round" fill="none" />
                <path d={`M${x-3},${32 - i%4*2} Q${x-7 - i%3},${16 - i%5*3} ${x-12},${0 - i%4*4}`} stroke="#3D7838" strokeWidth="1.6" strokeLinecap="round" fill="none" />
                <path d={`M${x+5},${25 - i%6*2} Q${x+3},${8 - i%3*3} ${x+1},${-8 - i%4*3}`} stroke="#5A9A55" strokeWidth="1.2" strokeLinecap="round" fill="none" />
              </g>
            ))}
            
            {/* Second row of grass */}
            {[15,48,78,112,145,178,210,245,278,312,345,378,412,445,478,512,545,578,612,645,678,712,745,778,812,845,878,912,945,978,1012,1045,1078,1112,1145,1178,1212,1245,1278,1312,1345,1378,1412,1445,1478,1512,1545,1578].map((x, i) => (
              <g key={`r2-${i}`} opacity={0.55 + (i % 3) * 0.08}>
                <path d={`M${x},${55 - i%5*3} Q${x - 5},${38 - i%4*4} ${x - 10},${22 - i%3*5}`} stroke="#4A8A48" strokeWidth="1.8" strokeLinecap="round" fill="none" />
                <path d={`M${x+10},${58 - i%3*2} Q${x+15},${40 - i%5*3} ${x+19},${25 - i%4*4}`} stroke="#5C9E5B" strokeWidth="1.4" strokeLinecap="round" fill="none" />
                <path d={`M${x+4},${52 - i%4*3} Q${x+2},${35 - i%3*4} ${x-1},${18 - i%5*3}`} stroke="#6BAE6B" strokeWidth="1.1" strokeLinecap="round" fill="none" />
              </g>
            ))}
            
            {/* Clover patches */}
            <g opacity="0.5">
              <circle cx="200" cy="45" r="3" fill="#7BBE6A" />
              <circle cx="205" cy="42" r="2.5" fill="#6BAE5A" />
              <circle cx="198" cy="41" r="2.8" fill="#8BC87A" />
            </g>
            <g opacity="0.45">
              <circle cx="680" cy="38" r="3.2" fill="#7BBE6A" />
              <circle cx="685" cy="35" r="2.6" fill="#6BAE5A" />
              <circle cx="677" cy="34" r="2.9" fill="#8BC87A" />
            </g>
            <g opacity="0.4">
              <circle cx="1150" cy="50" r="2.8" fill="#7BBE6A" />
              <circle cx="1155" cy="47" r="2.3" fill="#6BAE5A" />
              <circle cx="1148" cy="46" r="2.6" fill="#8BC87A" />
            </g>
            
            {/* Tiny wildflowers */}
            <circle cx="120" cy="35" r="3" fill="#FFB5C5" opacity="0.6" />
            <circle cx="122" cy="33" r="1.2" fill="#F4D35E" opacity="0.7" />
            <circle cx="450" cy="28" r="2.5" fill="#FFFFFF" opacity="0.5" />
            <circle cx="452" cy="26" r="1" fill="#F4D35E" opacity="0.6" />
            <circle cx="920" cy="42" r="2.8" fill="#C97BDB" opacity="0.45" />
            <circle cx="922" cy="40" r="1.1" fill="#F4D35E" opacity="0.55" />
            <circle cx="1380" cy="32" r="2.5" fill="#FFB5C5" opacity="0.5" />
            <circle cx="1382" cy="30" r="1" fill="#F4D35E" opacity="0.6" />
            <circle cx="580" cy="50" r="2" fill="#E85A5A" opacity="0.4" />
            <circle cx="1050" cy="55" r="2.2" fill="#FFFFFF" opacity="0.4" />
          </svg>
        </div>

        {/* ── ROOTS OVERLAY — visible above grassland, below text ── */}
        <div className="absolute inset-0 z-[28] pointer-events-none overflow-visible">
          <svg viewBox="0 0 1600 400" fill="none" preserveAspectRatio="xMidYMin slice" className="w-full h-full" style={{ overflow: 'visible' }}>
            <defs>
              <linearGradient id="rootGrad1" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#4A3A28" stopOpacity="0.7" />
                <stop offset="100%" stopColor="#4A3A28" stopOpacity="0" />
              </linearGradient>
              <linearGradient id="rootGrad2" x1="1" y1="0" x2="0" y2="0">
                <stop offset="0%" stopColor="#4A3A28" stopOpacity="0.65" />
                <stop offset="100%" stopColor="#4A3A28" stopOpacity="0" />
              </linearGradient>
            </defs>
            
            {/* Left roots — emerging from center, spreading left */}
            {/* Thick main root going far left, slightly wavy */}
            <path d="M800,-30 Q750,-18 680,-8 Q600,4 510,14 Q430,22 340,28 Q270,32 200,33" stroke="#5D4E37" strokeWidth="4.5" fill="none" opacity="0.5" strokeLinecap="round" />
            {/* Thinner root, different angle */}
            <path d="M795,-25 Q740,-12 670,2 Q580,18 480,30 Q400,38 310,42" stroke="#4A3A28" strokeWidth="3" fill="none" opacity="0.4" strokeLinecap="round" />
            {/* Short stubby root left */}
            <path d="M790,-20 Q755,-10 710,0 Q660,10 600,18" stroke="#5D4E37" strokeWidth="2.5" fill="none" opacity="0.35" strokeLinecap="round" />
            {/* Wispy thin root going far left-down */}
            <path d="M785,-28 Q720,-14 640,4 Q545,24 440,38 Q360,48 260,52" stroke="#3A2F1E" strokeWidth="2" fill="none" opacity="0.3" strokeLinecap="round" />
            {/* Very thin delicate root */}
            <path d="M792,-18 Q760,-6 720,6 Q670,16 620,22" stroke="#4A3A28" strokeWidth="1.5" fill="none" opacity="0.28" strokeLinecap="round" />
            
            {/* Right roots — emerging from center, spreading right */}
            {/* Thick main root going far right */}
            <path d="M810,-28 Q870,-16 940,-4 Q1020,8 1110,18 Q1190,26 1280,30 Q1350,32 1420,32" stroke="#5D4E37" strokeWidth="4" fill="none" opacity="0.48" strokeLinecap="round" />
            {/* Medium root, curving more */}
            <path d="M805,-22 Q860,-10 930,4 Q1010,18 1100,30 Q1180,38 1260,42" stroke="#4A3A28" strokeWidth="3.2" fill="none" opacity="0.42" strokeLinecap="round" />
            {/* Short thick root right */}
            <path d="M815,-18 Q860,-8 920,4 Q980,14 1050,22" stroke="#5D4E37" strokeWidth="2.8" fill="none" opacity="0.38" strokeLinecap="round" />
            {/* Long thin root right */}
            <path d="M808,-32 Q880,-18 960,0 Q1060,18 1170,32 Q1260,42 1360,46" stroke="#3A2F1E" strokeWidth="1.8" fill="none" opacity="0.3" strokeLinecap="round" />
            {/* Wispy right */}
            <path d="M812,-15 Q855,-4 910,8 Q960,16 1020,22" stroke="#4A3A28" strokeWidth="1.5" fill="none" opacity="0.25" strokeLinecap="round" />
            
            {/* Center roots — going slightly downward */}
            <path d="M798,-30 Q796,-10 794,12 Q792,28 790,40" stroke="#5D4E37" strokeWidth="2.5" fill="none" opacity="0.32" strokeLinecap="round" />
            <path d="M808,-30 Q810,-8 812,16 Q814,30 815,42" stroke="#4A3A28" strokeWidth="2" fill="none" opacity="0.28" strokeLinecap="round" />
            
            {/* Small branching sub-roots */}
            <path d="M680,-8 Q665,-2 645,6" stroke="#4A3A28" strokeWidth="1.2" fill="none" opacity="0.22" strokeLinecap="round" />
            <path d="M510,14 Q498,20 482,26" stroke="#3A2F1E" strokeWidth="1" fill="none" opacity="0.2" strokeLinecap="round" />
            <path d="M940,-4 Q955,2 975,10" stroke="#4A3A28" strokeWidth="1.2" fill="none" opacity="0.22" strokeLinecap="round" />
            <path d="M1110,18 Q1125,24 1145,30" stroke="#3A2F1E" strokeWidth="1" fill="none" opacity="0.2" strokeLinecap="round" />
          </svg>
        </div>

        <div className="container relative z-30 mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <Leaf className="h-4 w-4 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]" />
              <span className="font-heading font-semibold text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]">Jardim Mental</span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/terapeuta/auth" className="text-sm text-white font-medium hover:text-white/90 transition-colors drop-shadow-[0_1px_3px_rgba(0,0,0,0.6)]">
                Acesso para terapeutas
              </Link>
              <p className="text-sm text-white font-medium drop-shadow-[0_1px_3px_rgba(0,0,0,0.6)]">
                © 2026 Jardim Mental. Cuidando de quem precisa.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </main>
  );
}
